/**
 * How consistently anything was logged across a window.
 *
 * Adherence is the number the whole exercise depends on: a chart drawn from
 * four days out of ninety looks exactly like a chart drawn from ninety, and
 * nothing on it says which one you are reading. Coverage is what makes the
 * difference visible.
 */

import type { Entry } from '../model/entry.js'
import { dayKey } from '../model/timestamp.js'
import type { ResolvedRange } from './range.js'

export interface DayCount {
  readonly dayKey: string
  readonly count: number
}

export interface Adherence {
  readonly totalDays: number
  readonly loggedDays: number
  /** 0–1. */
  readonly coverage: number
  /**
   * Only days carrying entries.
   *
   * A day with no logging is absent from this list, never present with a count
   * of zero — the same distinction the series keeps, for the same reason.
   */
  readonly perDay: readonly DayCount[]
  /** The longest run of consecutive unlogged days inside the window. */
  readonly longestGapDays: number
  /** Consecutive logged days ending on the last day of the window. */
  readonly trailingStreakDays: number
}

function pad(value: number, width = 2): string {
  return String(Math.abs(value)).padStart(width, '0')
}

/**
 * Every day label from one to another, inclusive.
 *
 * Walks the range's **own** `fromDay` and `toDay` rather than re-deriving days
 * from its instants in some zone. Re-deriving is how the denominator ends up
 * disagreeing with the range control sitting directly above it: an "all time"
 * window resolved in an entry's zone starts at 23:00 UTC the previous evening,
 * and counting that in UTC produces one more day than the control says the
 * window is long.
 *
 * Pure calendar arithmetic on the labels, never instants stepped by 86,400,000
 * milliseconds: a DST day is 23 or 25 hours long, and fixed-millisecond
 * stepping across one either repeats a day or skips it. Because keys are
 * `YYYY-MM-DD`, string ordering is chronological ordering.
 */
function dayKeysBetween(fromDay: string, toDay: string): string[] {
  if (toDay < fromDay) {
    return []
  }
  const year = Number(fromDay.slice(0, 4))
  const month = Number(fromDay.slice(5, 7))
  const day = Number(fromDay.slice(8, 10))
  const keys: string[] = []

  for (let offset = 0; ; offset += 1) {
    const date = new Date(Date.UTC(year, month - 1, day + offset))
    const key =
      `${pad(date.getUTCFullYear(), 4)}-` +
      `${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
    if (key > toDay) {
      return keys
    }
    keys.push(key)
  }
}

/**
 * Coverage across a window.
 *
 * The denominator is the window's own length in days, so it always matches what
 * the range control says. Which day an entry *lands on* is decided by its own
 * captured zone — an entry logged at 23:30 abroad belongs to the day it was
 * made, not to whatever day it was back home.
 *
 * Those two can disagree at the edges: an entry inside the window by instant
 * can carry a captured day just outside it, after a flight across the date
 * line mid-window. Such an entry appears in `perDay`, which reports what was
 * recorded, and not in `loggedDays`, which counts days of the window — so
 * coverage stays between 0 and 1 rather than exceeding it.
 */
export function adherenceFor(entries: readonly Entry[], range: ResolvedRange): Adherence {
  const counts = new Map<string, number>()
  for (const entry of entries) {
    if (entry.at.epochMs < range.fromEpochMs || entry.at.epochMs > range.toEpochMs) {
      continue
    }
    const key = dayKey(entry.at)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  const allDays = dayKeysBetween(range.fromDay, range.toDay)

  let longestGapDays = 0
  let runningGap = 0
  for (const key of allDays) {
    runningGap = counts.has(key) ? 0 : runningGap + 1
    longestGapDays = Math.max(longestGapDays, runningGap)
  }

  let trailingStreakDays = 0
  for (let index = allDays.length - 1; index >= 0; index -= 1) {
    const key = allDays[index]
    if (key === undefined || !counts.has(key)) {
      break
    }
    trailingStreakDays += 1
  }

  const loggedDays = allDays.filter((key) => counts.has(key)).length

  return {
    totalDays: allDays.length,
    loggedDays,
    coverage: allDays.length === 0 ? 0 : loggedDays / allDays.length,
    perDay: [...counts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, count]) => ({ dayKey: key, count })),
    longestGapDays,
    trailingStreakDays,
  }
}
